import { useSuspenseQuery } from '@apollo/client';
import {
  QUERY_KEY_DATES,
  type KeyDatesQuery,
  type KeyDatesQueryVariables,
  type KeyDatePage,
} from '@edwin-group/people-graphql';
import { useMemo } from 'react';
import type { KeyDate, PaginationInfo } from '@/pages/key-dates/shared/types';
import { formatDateForGraphQL } from '@/pages/key-dates/shared/utils/format-date-for-graphql';
import { useAuthentication } from '@/hooks/useAuthentication';

export const useKeyDates = (limit: number = 100) => {
  const { schoolContext } = useAuthentication();
  const today = useMemo(() => {
    return formatDateForGraphQL(new Date()) || '';
  }, []);

  const { data } = useSuspenseQuery<KeyDatesQuery, KeyDatesQueryVariables>(QUERY_KEY_DATES, {
    variables: {
      limit,
      startDate: today,
      schoolContextDocumentId: schoolContext?.documentId ?? '',
    },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const keyDates = useMemo(() => {
    return (data?.keyDates?.nodes || []) as unknown as KeyDate[];
  }, [data]);

  const pagination = useMemo(() => {
    const pageInfo = data?.keyDates?.pageInfo;
    if (!pageInfo) return null;

    return {
      page: pageInfo.page || 1,
      pageCount: pageInfo.pageCount || 1,
      total: pageInfo.total || 0,
      pageSize: pageInfo.pageSize || limit,
    } as PaginationInfo;
  }, [data, limit]);

  const keyDatePage = useMemo(() => {
    return data?.keyDatePage as KeyDatePage | null;
  }, [data]);

  return {
    keyDates,
    pagination,
    keyDatePage,
  };
};
